import React from "react";

// `fields` = the content boxes this design actually uses (headline always comes from the offer name).
// The admin form shows only these; the player renders the same keys. Keys: sub|value|valueLabel|items|timer|finePrint
export const DESIGNS = [
  { key: "hero",         label: "Hero modal",     desc: "Big centre modal — main offers", fields: ["sub", "value", "valueLabel", "items", "timer", "finePrint"] },
  { key: "interstitial", label: "Interstitial",   desc: "Full centre, session start",     fields: ["sub", "value", "valueLabel", "items", "timer", "finePrint"] },
  { key: "sheet",        label: "Bottom sheet",   desc: "Slides up (mobile)",             fields: ["sub", "value", "valueLabel", "items", "timer", "finePrint"] },
  { key: "claim",        label: "Claim modal",    desc: "Celebratory claim",              fields: ["sub", "value", "valueLabel", "items", "finePrint"] },
  { key: "save",         label: "Save modal",     desc: "Churn rescue / at-risk",         fields: ["sub", "value", "valueLabel", "timer", "finePrint"] },
  { key: "toast",        label: "Corner toast",   desc: "Small, non-blocking",            fields: ["sub"] },
  { key: "progress",     label: "Progress nudge", desc: "Mission / streak progress",      fields: ["sub", "value", "valueLabel"] },
];

// palette + type scale copied from the SpinSaga_Popup_System design spec (.pop styles)
const HEADER = {
  hero: "linear-gradient(160deg,#7C3AED,#4C1D95)",
  interstitial: "linear-gradient(160deg,#2563EB,#1E3A8A)",
  claim: "linear-gradient(160deg,#16A34A,#15803D)",
  save: "linear-gradient(160deg,#DB2777,#9D174D)",
  sheet: "linear-gradient(160deg,#7C3AED,#4C1D95)",
};

// fixed per-design icon (design only — not admin content)
const ICON = { hero: "🎁", interstitial: "🎉", sheet: "🎁", claim: "🏆", save: "💎", toast: "🔔", progress: "🚀" };

const CloseX = ({ onClose }) => (
  <button onClick={onClose}
    style={{ position: "absolute", top: 10, right: 10, width: 26, height: 26, borderRadius: "50%", border: 0, cursor: "pointer",
      fontSize: 15, lineHeight: 1, display: "grid", placeItems: "center", zIndex: 4,
      background: "rgba(0,0,0,.10)", color: "#e9e4f5" }}
  >×</button>
);

const Item = ({ text }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 9, background: "#f7f5fc", border: "1px solid #E7E4F2", borderRadius: 10, padding: "9px 11px", fontSize: 12.5, fontWeight: 650, color: "#1E293B" }}>
    <span style={{ width: 22, height: 22, borderRadius: 7, background: "#ede7fb", color: "#5B21B6", display: "grid", placeItems: "center", fontSize: 13, flex: "none" }}>✓</span>
    <span>{text}</span>
  </div>
);

// live countdown to the offer's active-to date (the timer is driven by from/to, not a static value)
function Countdown({ endsAt }) {
  const compute = () => {
    if (!endsAt) return "";
    const diff = new Date(endsAt).getTime() - Date.now();
    if (isNaN(diff) || diff <= 0) return "00:00";
    const s = Math.floor(diff / 1000);
    const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
    const pad = (n) => String(n).padStart(2, "0");
    return h > 0 ? `${pad(h)}:${pad(m)}:${pad(sec)}` : `${pad(m)}:${pad(sec)}`;
  };
  const [t, setT] = React.useState(compute());
  React.useEffect(() => {
    const id = setInterval(() => setT(compute()), 1000);
    return () => clearInterval(id);
  }, [endsAt]);
  return <span style={{ fontFamily: "monospace", background: "#fdf1dd", border: "1px solid #f6dcae", borderRadius: 7, padding: "3px 8px" }}>{t}</span>;
}

// `content` is the optional middle content (from the offer JSON). Layout/styling is fixed per design.
export function PopupDesign({ design = "hero", ctaText = "Claim", content = {}, onClose, endsAt }) {
  const c = content || {};
  const headline = c.headline || "Your offer";
  const sub = c.sub || "";
  const value = c.value || "";
  const valueLabel = c.valueLabel || "";
  const items = Array.isArray(c.items) ? c.items : [];
  const timer = !!c.timer;
  const finePrint = c.finePrint || "";
  const icon = ICON[design] || "🎁";

  if (design === "toast") {
    return (
      <div style={{ position: "relative", display: "flex", alignItems: "center", gap: 12, background: "#fff", color: "#1E293B", borderRadius: 14, padding: "13px 14px", boxShadow: "0 10px 30px rgba(0,0,0,.25)", width: 340 }}>
        <div style={{ width: 42, height: 42, borderRadius: 11, background: "linear-gradient(160deg,#7C3AED,#4C1D95)", flex: "none", display: "grid", placeItems: "center", fontSize: 22 }}>{icon}</div>
        <div style={{ flex: 1, paddingRight: 14 }}><div style={{ fontWeight: 700, fontSize: 13.5 }}>{headline}</div>{sub && <div style={{ fontSize: 11.5, color: "#697089" }}>{sub}</div>}</div>
        <button style={{ border: 0, background: "#5B21B6", color: "#fff", borderRadius: 9, padding: "7px 12px", fontSize: 11.5, fontWeight: 650, flex: "none" }}>{ctaText}</button>
        <CloseX onClose={onClose} />
      </div>
    );
  }

  if (design === "progress") {
    return (
      <div style={{ position: "relative", width: 340, background: "#fff", color: "#1E293B", borderRadius: 16, overflow: "hidden", boxShadow: "0 24px 60px -12px rgba(0,0,0,.55)" }}>
        <CloseX onClose={onClose} />
        <div style={{ background: "linear-gradient(160deg,#7C3AED,#4C1D95)", color: "#fff", padding: "16px 20px", textAlign: "center" }}>
          <div style={{ fontWeight: 750, fontSize: 16 }}>{headline}</div>{sub && <div style={{ fontSize: 12.5, opacity: .85, marginTop: 4 }}>{sub}</div>}
        </div>
        <div style={{ padding: "16px 20px 20px" }}>
          {value && <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, fontWeight: 650, marginBottom: 4 }}><span>Progress</span><span>{value}</span></div>}
          <div style={{ height: 10, borderRadius: 6, background: "#ece9f6", overflow: "hidden", margin: "4px 0 8px" }}><div style={{ height: "100%", width: "60%", borderRadius: 6, background: "linear-gradient(90deg,#F5C451,#E0A92E)" }} /></div>
          {valueLabel && <div style={{ fontSize: 12.5, color: "#697089", textAlign: "center", margin: "8px 0 2px" }}>{valueLabel}</div>}
          <button style={{ marginTop: 14, width: "100%", border: 0, borderRadius: 12, padding: 13, background: "linear-gradient(180deg,#7C3AED,#5B21B6)", color: "#fff", fontWeight: 700, fontSize: 14, boxShadow: "0 4px 0 #3c1a82", letterSpacing: ".01em" }}>{ctaText}</button>
        </div>
      </div>
    );
  }

  const sheet = design === "sheet";
  return (
    <div style={{ position: "relative", width: 380, background: "#fff", color: "#1E293B", borderRadius: sheet ? "20px 20px 12px 12px" : 18, overflow: "hidden", boxShadow: "0 24px 60px -12px rgba(0,0,0,.55)" }}>
      <CloseX onClose={onClose} />
      {sheet && <div style={{ width: 38, height: 4, borderRadius: 3, background: "#dcd7ec", margin: "10px auto 0" }} />}
      <div style={{ background: HEADER[design] || HEADER.hero, color: "#fff", padding: "22px 20px 16px", textAlign: "center" }}>
        <div style={{ width: 56, height: 56, borderRadius: 16, background: "rgba(255,255,255,.16)", margin: "2px auto 12px", display: "grid", placeItems: "center", fontSize: 28 }}>{icon}</div>
        <div style={{ fontWeight: 750, fontSize: 18, letterSpacing: "-.01em", lineHeight: 1.2 }}>{headline}</div>{sub && <div style={{ fontSize: 12.5, opacity: .85, marginTop: 5 }}>{sub}</div>}
      </div>
      <div style={{ padding: "16px 20px 20px" }}>
        {value && (
          <div style={{ textAlign: "center", marginBottom: 14 }}>
            <div style={{ fontSize: 30, fontWeight: 800, color: "#4C1D95", letterSpacing: "-.02em", lineHeight: 1 }}>{value}</div>
            {valueLabel && <div style={{ fontSize: 11, color: "#697089", textTransform: "uppercase", letterSpacing: ".08em", fontWeight: 600, marginTop: 4 }}>{valueLabel}</div>}
          </div>
        )}
        {items.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 7, marginBottom: 14 }}>
            {items.map((t, i) => <Item key={i} text={t} />)}
          </div>
        )}
        {timer && endsAt && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, fontSize: 12, color: "#D97706", fontWeight: 650, marginBottom: 13 }}>
            ⏱ offer ends in <Countdown endsAt={endsAt} />
          </div>
        )}
        <button style={{ width: "100%", border: 0, borderRadius: 12, padding: 13, background: "linear-gradient(180deg,#F5C451,#E0A92E)", color: "#3a2400", fontWeight: 700, fontSize: 14, boxShadow: "0 4px 0 #c8951f", letterSpacing: ".01em" }}>{ctaText}</button>
        {finePrint && <div onClick={onClose} style={{ fontSize: 12, color: "#697089", fontWeight: 550, textAlign: "center", marginTop: 11, cursor: "pointer" }}>{finePrint}</div>}
      </div>
    </div>
  );
}

export default PopupDesign;
